import type { Layer, TemplateDoc } from '../template';
import { CAMERA_PRESET_MAP } from './camera';
import { FX_KIND_MAP } from './fx';
import { OPENERS } from './openers';
import { OUTROS } from './outros';
import { ACCENTS } from './accents';
import { ENTER_PRESET_MAP, EMPHASIS_PRESET_MAP, type Label, type PresetParams } from './presets';
import {
  docMotionSchema,
  type DocMotion,
  type MotionRole,
  type MotionPresetSpec,
  type CameraStep,
  type LayerMotion,
  type MotionFx,
  type MotionSplit,
} from './schema';
import { sceneTimeline } from './compile';
import { readingOrder } from './order';
import { syncToBeats } from './beats';

/**
 * "Animate" in one click: infer what each layer is (photo, headline, price, call to action…) and
 * choreograph it with a motion style. The result is ordinary per-layer motion, so it can be
 * fine-tuned in the editor afterwards, and re-running a style rebuilds it from scratch.
 */

interface RoleEnter {
  preset: string;
  duration?: number;
  easing?: string;
  params?: PresetParams;
  split?: Partial<MotionSplit>;
}
interface RoleEmphasis {
  preset: string;
  duration?: number;
  repeat?: number;
  gap?: number;
  /** Start with the scene (ambient moves) instead of after everything has entered. */
  fromStart?: boolean;
  params?: PresetParams;
}
export interface RoleMotion {
  enter?: RoleEnter;
  emphasis?: RoleEmphasis;
}

/** When a generated item starts: at a fixed time, or relative to when a role's layer entered. */
interface StyleTiming {
  start?: number;
  /** Start this long before the end of the video (outros). */
  beforeEnd?: number;
  /** Start once the first layer with this role has entered (skipped without one). */
  after?: MotionRole;
  offset?: number;
  /** ms, or the rest of the video. */
  duration: number | 'rest';
}

export interface StyleFx extends StyleTiming {
  kind: string;
  z?: 'front' | 'back';
  params?: PresetParams;
  /** The layer of this role is the fx target (underline, sweep). */
  target?: MotionRole;
  /** Centre the effect (burst, rings) on the layer of this role. */
  anchor?: MotionRole;
  /** Backdrops only show where the background is visible: skipped under full-bleed layers. */
  backdrop?: boolean;
  /** Skip when the design has a layer of this role. */
  unless?: MotionRole;
  /** Text of the first layer with one of these roles (title cards); the headline without one. */
  textFrom?: MotionRole[];
}

export interface StyleCamera extends StyleTiming {
  preset: string;
  params?: PresetParams;
}

export interface MotionStyle {
  id: string;
  /**
   * Openers add generated graphics (title card, covers, camera) before the design, outros at
   * the end of the video, accents in the middle, tied to elements of the design.
   */
  category?: 'style' | 'opener' | 'outro' | 'accent';
  /** Take the layer choreography (roles) of this style. */
  base?: string;
  label: Label;
  description: Label;
  /** Minimum video length. */
  duration: number;
  /** Delay between consecutive elements in reading order. */
  stagger: number;
  /** Time before the design starts entering (title card, cover). */
  intro?: number;
  /**
   * Layers leave together: at the very end of the video (loops), or as an outro starts. Layers
   * of the `keep` roles (with what they contain and what contains them) stay.
   */
  leave?: { preset: string; duration: number; params?: PresetParams; keep?: MotionRole[] };
  /** Outros: how long the ending lasts; the video grows by it. */
  span?: number;
  /** Outros: needs a layer of this role, otherwise the `fallback` outro plays. */
  requires?: MotionRole;
  fallback?: string;
  /**
   * Layer choreography. Outros and accents only use the emphasis: it replaces the style's (an
   * outro's starts with the ending).
   */
  roles?: Partial<Record<MotionRole, RoleMotion>>;
  fx?: StyleFx[];
  camera?: StyleCamera[];
}

/** Generated fx and camera ids start with this; re-animating replaces them, keeps the rest. */
export const AUTO_PREFIX = 'auto-';

const BASE_STYLES: MotionStyle[] = [
  {
    id: 'smooth',
    label: ['Smooth', 'Плавний', 'Płynny', 'Lin', 'Suave', 'Плавный'],
    description: [
      'Soft rises and fades, works for any design',
      'Мʼякі підйоми та появи, пасує будь-якому дизайну',
      'Miękkie wejścia, pasuje do każdego projektu',
      'Apariții line, potrivit oricărui design',
      'Entradas suaves, sirve para cualquier diseño',
      'Мягкие подъёмы и появления, подходит любому дизайну',
    ],
    duration: 5000,
    stagger: 150,
    roles: {
      background: { emphasis: { preset: 'breathe', fromStart: true, repeat: 0 } },
      photo: {
        enter: { preset: 'fade', duration: 900 },
        emphasis: { preset: 'ken-burns', fromStart: true },
      },
      container: { enter: { preset: 'fade', duration: 600 } },
      kicker: { enter: { preset: 'rise', duration: 600 } },
      headline: { enter: { preset: 'rise', duration: 800 } },
      subtitle: { enter: { preset: 'rise', duration: 700 } },
      body: { enter: { preset: 'rise', duration: 700 } },
      price: { enter: { preset: 'rise', duration: 700 }, emphasis: { preset: 'pulse', repeat: 1 } },
      badge: { enter: { preset: 'pop' } },
      cta: {
        enter: { preset: 'rise', duration: 700 },
        emphasis: { preset: 'pulse', repeat: 0, gap: 900 },
      },
      logo: { enter: { preset: 'fade', duration: 700 } },
      decor: { enter: { preset: 'fade', duration: 700 } },
    },
  },
  {
    id: 'energetic',
    label: ['Energetic', 'Енергійний', 'Energiczny', 'Energic', 'Enérgico', 'Энергичный'],
    description: [
      'Fast whips and pops for performance ads',
      'Швидкі ривки й попи для перформанс-реклами',
      'Szybkie wejścia do reklam performance',
      'Intrări rapide pentru reclame de performanță',
      'Entradas rápidas para anuncios de rendimiento',
      'Быстрые рывки и попы для перформанс-рекламы',
    ],
    duration: 4000,
    stagger: 100,
    camera: [{ preset: 'punch', after: 'headline', offset: -150, duration: 450 }],
    roles: {
      photo: { enter: { preset: 'zoom-in', duration: 600, params: { intensity: 1.2 } } },
      container: { enter: { preset: 'unfold', duration: 500 } },
      kicker: {
        enter: { preset: 'slide', duration: 450, params: { direction: 'right', distance: 80 } },
      },
      headline: { enter: { preset: 'whip', params: { direction: 'right' } } },
      subtitle: { enter: { preset: 'whip', params: { direction: 'left' } } },
      body: { enter: { preset: 'rise', duration: 450 } },
      price: { enter: { preset: 'pop' }, emphasis: { preset: 'shake', repeat: 0, gap: 1400 } },
      badge: {
        enter: { preset: 'spin', duration: 700 },
        emphasis: { preset: 'wiggle', repeat: 0, gap: 1500 },
      },
      cta: { enter: { preset: 'stretch' }, emphasis: { preset: 'heartbeat', repeat: 0, gap: 600 } },
      logo: { enter: { preset: 'pop', duration: 500 } },
      decor: { enter: { preset: 'zoom-in', duration: 500 } },
    },
  },
  {
    id: 'elegant',
    label: ['Elegant', 'Елегантний', 'Elegancki', 'Elegant', 'Elegante', 'Элегантный'],
    description: [
      'Slow masks and focus pulls for premium brands',
      'Повільні маски й фокус для преміум-брендів',
      'Powolne maski i ostrość dla marek premium',
      'Măști lente și focalizare pentru branduri premium',
      'Máscaras lentas y enfoque para marcas premium',
      'Медленные маски и фокус для премиум-брендов',
    ],
    duration: 6000,
    stagger: 260,
    camera: [{ preset: 'drift', start: 0, duration: 'rest', params: { intensity: 0.5 } }],
    roles: {
      background: {
        emphasis: { preset: 'ken-burns', fromStart: true, params: { intensity: 0.6 } },
      },
      photo: {
        enter: { preset: 'focus', duration: 1600 },
        emphasis: { preset: 'ken-burns', fromStart: true, params: { intensity: 0.7 } },
      },
      container: { enter: { preset: 'fade', duration: 1000 } },
      kicker: { enter: { preset: 'drift', duration: 1200 } },
      headline: { enter: { preset: 'lines-mask', duration: 1100 } },
      subtitle: { enter: { preset: 'mask-up', duration: 1000 } },
      body: { enter: { preset: 'fade', duration: 1000 } },
      price: { enter: { preset: 'mask-up', duration: 1000 } },
      badge: { enter: { preset: 'iris', duration: 1000 } },
      cta: {
        enter: { preset: 'fade', duration: 900 },
        emphasis: { preset: 'shine', repeat: 0, gap: 1600 },
      },
      logo: { enter: { preset: 'blur-in', duration: 1200 } },
      decor: { enter: { preset: 'wipe', duration: 1100 } },
    },
  },
  {
    id: 'kinetic',
    label: [
      'Kinetic type',
      'Кінетична типографіка',
      'Typografia kinetyczna',
      'Tipografie cinetică',
      'Tipografía cinética',
      'Кинетическая типографика',
    ],
    description: [
      'Letters and words move one by one',
      'Літери та слова рухаються по черзі',
      'Litery i słowa ruszają po kolei',
      'Literele și cuvintele se mișcă pe rând',
      'Letras y palabras se mueven una a una',
      'Буквы и слова движутся по очереди',
    ],
    duration: 5000,
    stagger: 140,
    roles: {
      photo: { enter: { preset: 'mask-up', duration: 900 } },
      container: { enter: { preset: 'unfold', duration: 600 } },
      kicker: { enter: { preset: 'typewriter' } },
      headline: { enter: { preset: 'letters-pop' } },
      subtitle: { enter: { preset: 'words-rise' } },
      body: { enter: { preset: 'words-rise' } },
      price: { enter: { preset: 'letters-drop' }, emphasis: { preset: 'tada', repeat: 1 } },
      badge: { enter: { preset: 'spin' } },
      cta: {
        enter: { preset: 'words-flip' },
        emphasis: { preset: 'rubber', repeat: 0, gap: 1200 },
      },
      logo: { enter: { preset: 'fade' } },
      decor: { enter: { preset: 'wipe', duration: 700 } },
    },
  },
  {
    id: 'product',
    label: [
      'Product hero',
      'Товар-герой',
      'Produkt w roli głównej',
      'Produs erou',
      'Producto héroe',
      'Товар-герой',
    ],
    description: [
      'The product flies in and floats, price and button pulse',
      'Товар вилітає й ширяє, ціна та кнопка пульсують',
      'Produkt wlatuje i unosi się, cena i przycisk pulsują',
      'Produsul intră și plutește, prețul și butonul pulsează',
      'El producto entra y flota, precio y botón laten',
      'Товар вылетает и парит, цена и кнопка пульсируют',
    ],
    duration: 5000,
    stagger: 160,
    fx: [{ kind: 'sweep', after: 'photo', offset: 200, duration: 1200, target: 'photo' }],
    camera: [{ preset: 'push-in', start: 0, duration: 'rest', params: { intensity: 0.35 } }],
    roles: {
      photo: {
        enter: { preset: 'focus', duration: 1100 },
        emphasis: { preset: 'float', fromStart: false, repeat: 0 },
      },
      container: { enter: { preset: 'fade' } },
      kicker: { enter: { preset: 'rise', duration: 600 } },
      headline: { enter: { preset: 'mask-up' } },
      subtitle: { enter: { preset: 'rise' } },
      body: { enter: { preset: 'rise' } },
      price: {
        enter: { preset: 'pop' },
        emphasis: { preset: 'pulse', repeat: 0, gap: 900, params: { intensity: 1.3 } },
      },
      badge: { enter: { preset: 'elastic' }, emphasis: { preset: 'wiggle', repeat: 0, gap: 1800 } },
      cta: { enter: { preset: 'rise' }, emphasis: { preset: 'heartbeat', repeat: 0, gap: 800 } },
      logo: { enter: { preset: 'fade' } },
      decor: { enter: { preset: 'zoom-in' } },
    },
  },
  {
    id: 'sale',
    label: ['Big sale', 'Розпродаж', 'Wyprzedaż', 'Reduceri', 'Rebajas', 'Распродажа'],
    description: [
      'Loud: glitches, shaking price, a pounding button',
      'Гучно: глітч, тремтлива ціна, кнопка, що бʼється',
      'Głośno: glitch, drżąca cena, pulsujący przycisk',
      'Zgomotos: glitch, preț care tremură, buton care bate',
      'Ruidoso: glitch, precio que tiembla, botón que late',
      'Громко: глитч, дрожащая цена, бьющаяся кнопка',
    ],
    duration: 4000,
    stagger: 110,
    fx: [
      {
        kind: 'burst',
        after: 'price',
        offset: -250,
        duration: 700,
        anchor: 'price',
        params: { count: 14, size: 0.7 },
      },
    ],
    camera: [
      { preset: 'punch', after: 'price', offset: -200, duration: 450, params: { intensity: 1.2 } },
    ],
    roles: {
      photo: { enter: { preset: 'zoom-in', duration: 550 } },
      container: { enter: { preset: 'unfold', duration: 450 } },
      kicker: { enter: { preset: 'glitch' } },
      headline: { enter: { preset: 'pop' }, emphasis: { preset: 'flash', repeat: 1 } },
      subtitle: { enter: { preset: 'slide', params: { direction: 'left', distance: 120 } } },
      body: { enter: { preset: 'slide', params: { direction: 'right', distance: 120 } } },
      price: {
        enter: { preset: 'elastic' },
        emphasis: { preset: 'shake', repeat: 0, gap: 1000, params: { intensity: 1.4 } },
      },
      badge: { enter: { preset: 'spin' }, emphasis: { preset: 'tada', repeat: 0, gap: 1200 } },
      cta: { enter: { preset: 'stretch' }, emphasis: { preset: 'heartbeat', repeat: 0, gap: 400 } },
      logo: { enter: { preset: 'fade', duration: 400 } },
      decor: { enter: { preset: 'pop' } },
    },
  },
  {
    id: 'playful',
    label: ['Playful', 'Грайливий', 'Zabawny', 'Jucăuș', 'Juguetón', 'Игривый'],
    description: [
      'Bounces, swings and jelly',
      'Стрибки, гойдання й желе',
      'Podskoki, huśtanie i galaretka',
      'Sărituri, legănări și jeleu',
      'Rebotes, balanceos y gelatina',
      'Прыжки, качели и желе',
    ],
    duration: 5000,
    stagger: 140,
    fx: [
      { kind: 'shapes', start: 0, duration: 'rest', z: 'back', backdrop: true },
      { kind: 'confetti', after: 'cta', offset: 0, duration: 2200, params: { count: 60 } },
    ],
    roles: {
      photo: {
        enter: { preset: 'pop' },
        emphasis: { preset: 'float', repeat: 0, params: { intensity: 1.5 } },
      },
      container: { enter: { preset: 'stretch' } },
      kicker: { enter: { preset: 'swing' } },
      headline: { enter: { preset: 'letters-drop' } },
      subtitle: { enter: { preset: 'drop' } },
      body: { enter: { preset: 'swing' } },
      price: { enter: { preset: 'elastic' }, emphasis: { preset: 'jello', repeat: 0, gap: 1400 } },
      badge: { enter: { preset: 'swing' }, emphasis: { preset: 'swing', repeat: 0, gap: 1400 } },
      cta: { enter: { preset: 'elastic' }, emphasis: { preset: 'bounce', repeat: 0, gap: 900 } },
      logo: { enter: { preset: 'pop' } },
      decor: { enter: { preset: 'roll' } },
    },
  },
  {
    id: 'cinematic',
    label: ['Cinematic', 'Кіно', 'Kinowy', 'Cinematic', 'Cinematográfico', 'Кино'],
    description: [
      'Slow camera moves and letters out of blur',
      'Повільна камера й літери з розмиття',
      'Powolna kamera i litery z rozmycia',
      'Cameră lentă și litere din ceață',
      'Cámara lenta y letras desde el desenfoque',
      'Медленная камера и буквы из размытия',
    ],
    duration: 7000,
    stagger: 300,
    camera: [{ preset: 'push-in', start: 0, duration: 'rest', params: { intensity: 0.4 } }],
    roles: {
      background: { emphasis: { preset: 'ken-burns', fromStart: true } },
      photo: {
        enter: { preset: 'blur-in', duration: 1500 },
        emphasis: { preset: 'ken-burns', fromStart: true },
      },
      container: { enter: { preset: 'fade', duration: 1200 } },
      kicker: { enter: { preset: 'fade', duration: 1200 } },
      headline: { enter: { preset: 'letters-blur', duration: 1000 } },
      subtitle: { enter: { preset: 'blur-in', duration: 1100 } },
      body: { enter: { preset: 'blur-in', duration: 1100 } },
      price: { enter: { preset: 'focus' } },
      badge: { enter: { preset: 'blur-in' } },
      cta: {
        enter: { preset: 'fade', duration: 1000 },
        emphasis: { preset: 'shine', repeat: 0, gap: 2000 },
      },
      logo: { enter: { preset: 'fade', duration: 1200 } },
      decor: { enter: { preset: 'fade', duration: 1200 } },
    },
  },
  {
    id: 'minimal',
    label: ['Minimal', 'Мінімалізм', 'Minimalizm', 'Minimalist', 'Minimalista', 'Минимализм'],
    description: [
      'Quiet fades in reading order, nothing else',
      'Тихі появи за порядком читання',
      'Ciche pojawienia w kolejności czytania',
      'Apariții discrete în ordinea citirii',
      'Apariciones discretas en orden de lectura',
      'Тихие появления в порядке чтения, ничего лишнего',
    ],
    duration: 5000,
    stagger: 250,
    roles: {
      photo: { enter: { preset: 'fade', duration: 1000 } },
      container: { enter: { preset: 'fade', duration: 800 } },
      kicker: { enter: { preset: 'fade', duration: 800 } },
      headline: {
        enter: { preset: 'drift', duration: 1000, params: { direction: 'up', distance: 8 } },
      },
      subtitle: {
        enter: { preset: 'drift', duration: 1000, params: { direction: 'up', distance: 8 } },
      },
      body: { enter: { preset: 'fade', duration: 900 } },
      price: { enter: { preset: 'fade', duration: 900 } },
      badge: { enter: { preset: 'fade', duration: 900 } },
      cta: { enter: { preset: 'fade', duration: 900 } },
      logo: { enter: { preset: 'fade', duration: 900 } },
      decor: { enter: { preset: 'fade', duration: 900 } },
    },
  },
  {
    id: 'reveal',
    label: ['Reveal', 'Розкриття', 'Odsłona', 'Dezvăluire', 'Revelación', 'Раскрытие'],
    description: [
      'Wipes, irises and masks open the design piece by piece',
      'Шторки, діафрагми й маски відкривають дизайн частинами',
      'Kurtyny, przesłony i maski odsłaniają projekt',
      'Cortine, diafragme și măști dezvăluie designul',
      'Cortinillas, iris y máscaras revelan el diseño',
      'Шторки, диафрагмы и маски открывают дизайн по частям',
    ],
    duration: 5000,
    stagger: 180,
    roles: {
      photo: { enter: { preset: 'wipe', duration: 1000, params: { direction: 'right' } } },
      container: { enter: { preset: 'split', duration: 800 } },
      kicker: { enter: { preset: 'mask-up' } },
      headline: { enter: { preset: 'lines-mask' } },
      subtitle: { enter: { preset: 'mask-up' } },
      body: { enter: { preset: 'mask-up' } },
      price: { enter: { preset: 'iris' } },
      badge: { enter: { preset: 'iris' } },
      cta: {
        enter: { preset: 'split', params: { direction: 'right' } },
        emphasis: { preset: 'pulse', repeat: 0, gap: 1200 },
      },
      logo: { enter: { preset: 'wipe', params: { direction: 'up' } } },
      decor: { enter: { preset: 'wipe', params: { direction: 'down' } } },
    },
  },
];

export const MOTION_STYLES: MotionStyle[] = [...BASE_STYLES, ...OPENERS];
export const MOTION_STYLE_MAP = new Map(MOTION_STYLES.map((s) => [s.id, s]));
export const OUTRO_STYLES = OUTROS;
export const OUTRO_MAP = new Map(OUTROS.map((s) => [s.id, s]));
export const ACCENT_STYLES = ACCENTS;
export const ACCENT_MAP = new Map(ACCENTS.map((s) => [s.id, s]));

const six = (s: string): Label => [s, s, s, s, s, s];

/** A custom preset (see custom.ts) as a motion style. */
export function presetStyle(spec: MotionPresetSpec): MotionStyle {
  return {
    id: spec.id,
    category: spec.category,
    base: spec.base,
    label: six(spec.name),
    description: six(spec.description),
    duration: spec.duration,
    stagger: spec.stagger,
    intro: spec.intro,
    span: spec.span,
    requires: spec.requires,
    fallback: spec.fallback,
    leave: spec.leave,
    roles: spec.roles,
    fx: spec.fx,
    camera: spec.camera,
  };
}

/** Custom presets kept in a document, as motion styles. */
export function customStyles(motion: DocMotion | null | undefined): MotionStyle[] {
  return (motion?.custom ?? []).map(presetStyle);
}

/** A built-in style, opener, outro or accent, or one of the given custom ones. */
function styleLookup(custom: MotionStyle[] = []) {
  const own = new Map(custom.map((s) => [s.id, s]));
  return (id: string | null | undefined): MotionStyle | undefined =>
    id
      ? (MOTION_STYLE_MAP.get(id) ?? OUTRO_MAP.get(id) ?? ACCENT_MAP.get(id) ?? own.get(id))
      : undefined;
}

function rolesOf(style: MotionStyle): Partial<Record<MotionRole, RoleMotion>> {
  if (style.roles) return style.roles;
  const base = style.base ? MOTION_STYLE_MAP.get(style.base) : undefined;
  return base && base !== style ? rolesOf(base) : {};
}

const NAME_ROLES: Array<[RegExp, MotionRole]> = [
  [/(^|[_\s-])(bg|background|backdrop)|фон/i, 'background'],
  [/logo|brand|лого|бренд/i, 'logo'],
  [/cta|button|btn|кнопк|action/i, 'cta'],
  [/price|offer|discount|sale|deal|цен|ціна|ціни|скидк|знижк|акци/i, 'price'],
  [/badge|sticker|stamp|label|бейдж|наклейк|стикер/i, 'badge'],
  [/kicker|eyebrow|tagline|overline|надзаг/i, 'kicker'],
  [/headline|title|heading|заголов/i, 'headline'],
  [/subtitle|subhead|lead|description|подзаг|підзаг|опис/i, 'subtitle'],
  [/photo|image|picture|product|avatar|illustration|фото|товар|картин|зображ/i, 'photo'],
  [/overlay|decor|shape|circle|line|blob|декор/i, 'decor'],
];

const PRICE_TEXT =
  /(\d[\d\s.,]*\s?(%|₽|₴|\$|€|£|zł|lei|руб|грн|uah|rub|usd|eur))|((\$|€|£)\s?\d)|(-\s?\d+\s?%)/i;

/** Best guess of what a layer is, from its name, type, size and text. */
export function inferRole(layer: Layer, doc: TemplateDoc, maxFontSize: number): MotionRole {
  const canvasArea = doc.width * doc.height;
  const area = (layer.width * layer.height) / canvasArea;
  for (const [re, role] of NAME_ROLES)
    if (re.test(layer.name)) {
      // A full-canvas photo named "photo" is still a background for motion purposes
      if (role === 'photo' && area > 0.85 && !layer.parentId) return 'background';
      return role;
    }
  switch (layer.type) {
    case 'group':
      return 'container';
    case 'image':
      if (area > 0.85 && !layer.parentId) return 'background';
      if (area < 0.05) return 'logo';
      return 'photo';
    case 'shape':
      if (area > 0.85 && !layer.parentId) return 'background';
      return 'decor';
    case 'qr':
      return 'decor';
    case 'text': {
      if (PRICE_TEXT.test(layer.text)) return 'price';
      if (layer.background && layer.text.length <= 28) return 'cta';
      if (layer.fontSize >= maxFontSize * 0.98) return 'headline';
      if (layer.fontSize >= maxFontSize * 0.6) return 'subtitle';
      return 'body';
    }
  }
}

function pieceCount(layer: Layer, by: MotionSplit['by']): number {
  if (layer.type !== 'text') return 1;
  const t = layer.text.trim();
  if (by === 'char') return Math.max(1, t.replace(/\s+/g, '').length);
  if (by === 'word') return Math.max(1, t.split(/\s+/).length);
  if (by === 'line') return Math.max(1, Math.min(6, Math.ceil(t.length / 24)));
  return 1;
}

export interface AutoAnimateOptions {
  /** Role overrides by layer id (from the editor). */
  roles?: Record<string, MotionRole>;
  /**
   * Opener to add on top of the layer style; null for none. Defaults to the document's current
   * opener. Passing an opener as the style id applies that opener with its own layer style.
   */
  opener?: string | null;
  /** Outro; null for none. Defaults to the document's current one. */
  outro?: string | null;
  /** Accents in the middle. Defaults to the document's current ones. */
  accents?: string[];
  /**
   * Custom presets the ids may refer to (a workspace's), on top of the ones the document
   * already keeps. Those in use are copied into the document.
   */
  presets?: MotionPresetSpec[];
}

/**
 * Layer style and opener for a style id (layer style or opener) and an optional opener. Custom
 * presets (`custom`) resolve like built-in ones.
 */
export function resolveMotionChoice(
  styleId: string | undefined,
  opener?: string | null,
  custom: MotionStyle[] = [],
): { style: MotionStyle; opener: MotionStyle | null } {
  const find = styleLookup(custom);
  const found = find(styleId);
  const picked =
    found && (found.category ?? 'style') !== 'outro' && found.category !== 'accent'
      ? found
      : MOTION_STYLES[0];
  if (picked.category === 'opener') {
    const base = MOTION_STYLE_MAP.get(picked.base ?? '');
    return {
      style: base && base.category !== 'opener' ? base : MOTION_STYLES[0],
      opener: picked,
    };
  }
  const o = find(opener);
  return { style: picked, opener: o?.category === 'opener' ? o : null };
}

export const LAYER_STYLES = MOTION_STYLES.filter((s) => s.category !== 'opener');
export const OPENER_STYLES = MOTION_STYLES.filter((s) => s.category === 'opener');

/**
 * Return a copy of the document animated with the given style. Scenes and scene membership are
 * kept; enter, exit and emphasis of every layer are rebuilt.
 */
export function autoAnimate(
  doc: TemplateDoc,
  styleId: string,
  opts: AutoAnimateOptions = {},
): TemplateDoc {
  const specs = new Map(
    [...(doc.motion?.custom ?? []), ...(opts.presets ?? [])].map((s) => [s.id, s]),
  );
  const custom = [...specs.values()].map(presetStyle);
  const find = styleLookup(custom);
  const { style, opener } = resolveMotionChoice(
    styleId,
    opts.opener === undefined ? doc.motion?.opener : opts.opener,
    custom,
  );
  const outroPick = find(opts.outro === undefined ? doc.motion?.outro : opts.outro);
  const pickedOutro = outroPick?.category === 'outro' ? outroPick : undefined;
  const accents = (opts.accents ?? doc.motion?.accents ?? []).flatMap((id) => {
    const a = find(id);
    return a?.category === 'accent' ? [a] : [];
  });
  // Accents replace the style's emphasis of their roles
  const roles = { ...rolesOf(style) };
  for (const a of accents)
    for (const [role, rm] of Object.entries(a.roles ?? {}) as Array<[MotionRole, RoleMotion]>)
      if (rm.emphasis) roles[role] = { ...roles[role], emphasis: rm.emphasis };
  // Only the custom presets in use stay with the template
  const inUse = [style, opener, pickedOutro, ...accents].flatMap((s) => {
    const spec = s && specs.get(s.id);
    return spec ? [spec] : [];
  });
  const motion = docMotionSchema.parse({
    ...(doc.motion ?? {}),
    style: style.id,
    opener: opener?.id ?? null,
    outro: pickedOutro?.id ?? null,
    accents: accents.map((a) => a.id),
    custom: inUse.length ? inUse : undefined,
  });
  const intro = opener?.intro ?? 0;
  const minDuration = Math.max(style.duration, opener?.duration ?? 0);
  const firstScene = motion.scenes[0]?.id;
  if (intro && motion.scenes.length)
    motion.scenes[0].duration = Math.max(motion.scenes[0].duration, intro + 2000);
  const maxFont = Math.max(1, ...doc.layers.map((l) => (l.type === 'text' ? l.fontSize : 0)));
  const hiddenStacks = new Set(
    doc.layers.filter((l) => l.type === 'group' && !l.visible).map((l) => l.id),
  );
  const ordered = readingOrder(doc).filter(
    (l) => l.visible && !(l.parentId && hiddenStacks.has(l.parentId)),
  );

  // Per scene: index in the sequence and when everything has entered
  const seq = new Map<string, number>();
  const enterEnd = new Map<string, number>();
  const result = new Map<string, LayerMotion>();
  /** First layer of each role in reading order, and when each layer has fully entered. */
  const roleLayer = new Map<MotionRole, Layer>();
  const enteredAt = new Map<string, number>();
  const layerRole = new Map<string, MotionRole>();
  const scaled = Math.max(0.6, Math.min(1, Math.min(doc.width, doc.height) / 600));

  for (const layer of ordered) {
    const role = opts.roles?.[layer.id] ?? inferRole(layer, doc, maxFont);
    if (!roleLayer.has(role)) roleLayer.set(role, layer);
    layerRole.set(layer.id, role);
    const rm = roles[role];
    const scene = layer.motion?.scene;
    const key = scene ?? '';
    const m: LayerMotion = { scene, enter: null, exit: null, emphasis: null };
    // A plain (transparent) stack only arranges its children: animate the children instead
    const transparentStack = layer.type === 'group' && !layer.fill;
    if (rm?.enter && !transparentStack) {
      const preset = ENTER_PRESET_MAP.get(rm.enter.preset);
      if (preset) {
        const i = seq.get(key) ?? 0;
        seq.set(key, i + 1);
        const shift = !scene || scene === firstScene ? intro : 0;
        const delay = role === 'background' ? 0 : shift + 200 + i * style.stagger;
        const duration = Math.round(
          (rm.enter.duration ?? preset.duration) * (preset.split ? 1 : scaled),
        );
        const splitDefaults = preset.split
          ? { by: 'none', stagger: 40, order: 'start', ...preset.split, ...rm.enter.split }
          : undefined;
        let groupDuration = duration;
        if (splitDefaults && layer.type === 'text') {
          const n = pieceCount(layer, splitDefaults.by as MotionSplit['by']);
          // Keep long texts from crawling in: the whole group enters within ~1.6 s
          const maxStagger =
            n > 1 ? Math.max(8, (1600 - duration) / (n - 1)) : splitDefaults.stagger;
          splitDefaults.stagger = Math.round(Math.min(splitDefaults.stagger, maxStagger));
          groupDuration = duration + (n - 1) * splitDefaults.stagger;
          m.split = splitDefaults as MotionSplit;
        }
        m.enter = {
          preset: preset.id,
          delay,
          duration,
          easing: rm.enter.easing,
          params: rm.enter.params,
        };
        enterEnd.set(key, Math.max(enterEnd.get(key) ?? 0, delay + groupDuration));
        enteredAt.set(layer.id, delay + groupDuration);
      }
    }
    if (rm?.emphasis && EMPHASIS_PRESET_MAP.has(rm.emphasis.preset)) {
      const preset = EMPHASIS_PRESET_MAP.get(rm.emphasis.preset)!;
      m.emphasis = {
        preset: preset.id,
        // Placeholder; fixed once we know when everything has entered
        delay: rm.emphasis.fromStart ? 0 : -1,
        duration: rm.emphasis.duration ?? preset.duration,
        repeat: rm.emphasis.repeat ?? preset.repeat,
        gap: rm.emphasis.gap ?? 0,
        params: rm.emphasis.params,
      };
    }
    result.set(layer.id, m);
  }

  // An outro whose element the design lacks plays its fallback
  const outro =
    pickedOutro?.requires && !roleLayer.has(pickedOutro.requires)
      ? (OUTRO_MAP.get(pickedOutro.fallback ?? '') ?? null)
      : (pickedOutro ?? null);
  const span = outro?.span ?? 0;
  const lastScene = motion.scenes[motion.scenes.length - 1];

  // Video length: the style's minimum, or long enough to see everything after it entered; the
  // ending comes on top
  if (!motion.scenes.length) {
    motion.duration = Math.max(
      minDuration + span,
      Math.ceil(((enterEnd.get('') ?? 0) + 1800 + span) / 500) * 500,
    );
  } else if (span) {
    lastScene.duration = Math.max(
      lastScene.duration,
      Math.ceil(((enterEnd.get(lastScene.id) ?? 0) + 1500 + span) / 500) * 500,
    );
  }
  const { scenes, duration } = sceneTimeline(motion);
  const sceneLength = (id: string | undefined) => {
    const s = id ? scenes.find((x) => x.id === id) : undefined;
    return s ? s.end - s.start : duration;
  };
  const sceneStart = (id: string | undefined) =>
    (id ? scenes.find((x) => x.id === id)?.start : undefined) ?? 0;

  for (const [id, m] of result) {
    if (!m.emphasis) continue;
    const key = m.scene ?? '';
    const length = sceneLength(m.scene);
    if (m.emphasis.delay < 0)
      m.emphasis.delay = Math.min(length - 200, (enterEnd.get(key) ?? 0) + 250);
    const preset = EMPHASIS_PRESET_MAP.get(m.emphasis.preset)!;
    // Ambient one-shot moves (Ken Burns) span the whole scene
    if (preset.ambient && m.emphasis.repeat === 1)
      m.emphasis.duration = Math.max(1000, length - m.emphasis.delay);
    m.emphasis.delay = Math.max(0, m.emphasis.delay);
    result.set(id, m);
  }

  // The ending: layers on screen at the end leave as the outro starts (or together at the very
  // end, for loops), except the kept ones, which take the outro's emphasis
  const outroStart = duration - span;
  const inEnding = (m: LayerMotion) => !m.scene || m.scene === lastScene?.id;
  const byId = new Map(doc.layers.map((l) => [l.id, l]));
  const ancestors = (l: Layer): Layer[] => {
    const out: Layer[] = [];
    for (let p = l.parentId ? byId.get(l.parentId) : undefined; p && out.length < 50;)
      p = (out.push(p), p.parentId ? byId.get(p.parentId) : undefined);
    return out;
  };
  const leave = outro ? outro.leave : style.leave;
  if (leave && ENTER_PRESET_MAP.has(leave.preset)) {
    const keepRoles = new Set<MotionRole>(leave.keep ?? []);
    const keptByRole = (l: Layer) => keepRoles.has(layerRole.get(l.id)!);
    // A kept layer keeps what contains it and what it contains (a button keeps its label)
    const kept = new Set<string>();
    for (const l of ordered)
      if (keptByRole(l) || ancestors(l).some(keptByRole)) {
        kept.add(l.id);
        for (const a of ancestors(l)) kept.add(a.id);
      }
    const leaves = (l: Layer) => {
      const m = result.get(l.id);
      return Boolean(m && inEnding(m) && !kept.has(l.id) && !(l.type === 'group' && !l.fill));
    };
    for (const l of ordered) {
      // A layer inside one that leaves goes with it
      if (!leaves(l) || ancestors(l).some(leaves)) continue;
      const m = result.get(l.id)!;
      m.exit = {
        preset: leave.preset,
        delay: outro ? Math.max(0, span - leave.duration) : 0,
        duration: leave.duration,
        params: leave.params,
      };
    }
  }
  if (outro?.roles) {
    for (const [id, m] of result) {
      const role = layerRole.get(id)!;
      const em = inEnding(m) ? outro.roles[role]?.emphasis : undefined;
      const preset = em && EMPHASIS_PRESET_MAP.get(em.preset);
      if (!em || !preset) continue;
      // A plain stack of the role beats through the layer of that role inside it, not twice
      const layer = byId.get(id)!;
      if (
        layer.type === 'group' &&
        !layer.fill &&
        ordered.some((l) => layerRole.get(l.id) === role && ancestors(l).includes(layer))
      )
        continue;
      m.emphasis = {
        preset: preset.id,
        delay: Math.max(0, outroStart - sceneStart(m.scene) + 300),
        duration: em.duration ?? preset.duration,
        repeat: em.repeat ?? preset.repeat,
        gap: em.gap ?? 0,
        params: em.params,
      };
    }
  }

  // Generated graphics and camera: replace the previous style's, keep hand-made ones
  const total = duration;
  /**
   * The element of a role that graphics attach to: a plain stack named after the role (an
   * "offer" stack with a price and a button) stands for the layer of that role inside it.
   */
  const roleTarget = (role: MotionRole): Layer | undefined => {
    const layer = roleLayer.get(role);
    if (!layer || layer.type !== 'group' || layer.fill) return layer;
    return (
      ordered.find(
        (l) => l !== layer && layerRole.get(l.id) === role && ancestors(l).includes(layer),
      ) ?? layer
    );
  };
  /**
   * When the element of a role has entered: the layer itself, or what it contains when it is a
   * plain stack that does not move (a price stack enters with its numbers).
   */
  const enteredRole = (role: MotionRole): { layer: Layer; at: number } | null => {
    const layer = roleTarget(role);
    if (!layer) return null;
    const own = enteredAt.get(layer.id);
    if (own !== undefined) return { layer, at: own };
    let best: { layer: Layer; at: number } | null = null;
    for (const l of ordered) {
      const at = enteredAt.get(l.id);
      if (at !== undefined && ancestors(l).includes(layer) && (!best || at > best.at))
        best = { layer: l, at };
    }
    return best;
  };
  const timing = (t: StyleTiming): { start: number; duration: number } | null => {
    let start = t.beforeEnd !== undefined ? total - t.beforeEnd : (t.start ?? 0);
    if (t.after) {
      const entered = enteredRole(t.after);
      if (!entered) return null;
      start = sceneStart(result.get(entered.layer.id)?.scene) + entered.at + (t.offset ?? 0);
    }
    start = Math.max(0, Math.min(total - 100, start));
    const duration = t.duration === 'rest' ? total - start : t.duration;
    return duration >= 50 ? { start, duration } : null;
  };
  const covered = backgroundCovered(doc);
  const fx: MotionFx[] = (motion.fx ?? []).filter((f) => !f.id.startsWith(AUTO_PREFIX));
  const fxItems = [
    ...(style.fx ?? []).map((f, i) => ({ f, id: `${AUTO_PREFIX}fx-${i}` })),
    ...(opener?.fx ?? []).map((f, i) => ({ f, id: `${AUTO_PREFIX}fx-o${i}` })),
    ...accents.flatMap((a, j) =>
      (a.fx ?? []).map((f, i) => ({ f, id: `${AUTO_PREFIX}fx-a${j}-${i}` })),
    ),
    ...(outro?.fx ?? []).map((f, i) => ({ f, id: `${AUTO_PREFIX}fx-e${i}` })),
  ];
  fxItems.forEach(({ f, id }) => {
    if (!FX_KIND_MAP.has(f.kind)) return;
    if (f.unless && roleLayer.has(f.unless)) return;
    if (f.backdrop && covered) return;
    const tm = timing(f);
    if (!tm) return;
    const params: PresetParams = { ...(f.params ?? {}) };
    if (f.target) {
      const l = roleTarget(f.target);
      if (!l) return;
      params.target = l.id;
    }
    if (f.anchor) {
      const l = roleTarget(f.anchor);
      if (l) params.anchor = l.id;
    }
    if (f.textFrom) {
      const text = f.textFrom
        .map((r) => roleLayer.get(r))
        .map((l) => l && textOf(l))
        .find(Boolean);
      if (text) params.text = text;
    }
    fx.push({
      id,
      kind: f.kind,
      start: tm.start,
      duration: tm.duration,
      z: f.z ?? FX_KIND_MAP.get(f.kind)!.z,
      params,
    });
  });
  const camera: CameraStep[] = (motion.camera ?? []).filter((c) => !c.id.startsWith(AUTO_PREFIX));
  // The opener's camera wins: a style move of the same kind (a continuous move, or the same
  // beat preset) would double up with it
  const openerCam = opener?.camera ?? [];
  const clashes = (c: StyleCamera) =>
    openerCam.some((o) =>
      c.duration === 'rest'
        ? o.duration === 'rest'
        : o.duration !== 'rest' && o.preset === c.preset,
    );
  const camItems = [
    ...(style.camera ?? [])
      .map((c, i) => ({ c, id: `${AUTO_PREFIX}cam-${i}` }))
      .filter(({ c }) => !clashes(c)),
    ...openerCam.map((c, i) => ({ c, id: `${AUTO_PREFIX}cam-o${i}` })),
    ...accents.flatMap((a, j) =>
      (a.camera ?? []).map((c, i) => ({ c, id: `${AUTO_PREFIX}cam-a${j}-${i}` })),
    ),
    ...(outro?.camera ?? []).map((c, i) => ({ c, id: `${AUTO_PREFIX}cam-e${i}` })),
  ];
  camItems.forEach(({ c, id }) => {
    if (!CAMERA_PRESET_MAP.has(c.preset)) return;
    const tm = timing(c);
    if (!tm) return;
    camera.push({ id, preset: c.preset, ...tm, params: c.params });
  });
  motion.fx = fx;
  motion.camera = camera;

  /** Words of a layer: a text, or the first text inside a stack (a button's label). */
  function textOf(l: Layer): string {
    if (l.type === 'text') return l.text.replace(/\s+/g, ' ').trim();
    const inner = ordered.find((x) => x.type === 'text' && ancestors(x).includes(l));
    return inner?.type === 'text' ? inner.text.replace(/\s+/g, ' ').trim() : '';
  }

  const animated: TemplateDoc = {
    ...doc,
    motion,
    layers: doc.layers.map((l) => {
      const m = result.get(l.id);
      // Hand-placed key frames survive a restyle
      const keys = l.motion?.keys?.length ? { keys: l.motion.keys } : {};
      if (!m) {
        // Hidden or skipped layers keep only their scene membership
        return l.motion?.scene || keys.keys
          ? { ...l, motion: { scene: l.motion?.scene, ...keys } }
          : { ...l, motion: undefined };
      }
      return { ...l, motion: { ...m, ...keys } };
    }),
  };
  // With music: moves land on the beat
  return motion.audio?.bpm && motion.audio.sync ? syncToBeats(animated) : animated;
}

/** A full-bleed opaque layer hides backdrops drawn between the background and the layers. */
function backgroundCovered(doc: TemplateDoc): boolean {
  const canvas = doc.width * doc.height;
  return doc.layers.some((l) => {
    if (l.parentId || !l.visible || l.opacity < 0.9) return false;
    const covers =
      l.x <= doc.width * 0.04 &&
      l.y <= doc.height * 0.04 &&
      l.x + l.width >= doc.width * 0.96 &&
      l.y + l.height >= doc.height * 0.96 &&
      (l.width * l.height) / canvas > 0.85;
    if (!covers) return false;
    if (l.type === 'image') return true;
    if (l.type === 'shape' || l.type === 'group') return Boolean(l.fill);
    return false;
  });
}

/** Roles of all layers, for the editor's "what is this" overrides. */
export function inferRoles(doc: TemplateDoc): Record<string, MotionRole> {
  const maxFont = Math.max(1, ...doc.layers.map((l) => (l.type === 'text' ? l.fontSize : 0)));
  return Object.fromEntries(doc.layers.map((l) => [l.id, inferRole(l, doc, maxFont)]));
}
